import * as React from 'react';
import { Volume2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface AudioDictationFieldsProps {
  audioText: string;
  audioUrl?: string | null;
  singleCorrectAnswer: string;
  onChange: (updates: Partial<ActivityFormData>) => void;
  onGenerateAudio?: () => Promise<string>;
  errors?: {
    audioText?: string;
    singleCorrectAnswer?: string;
  };
}

export function AudioDictationFields({
  audioText,
  audioUrl,
  singleCorrectAnswer,
  onChange,
  onGenerateAudio,
  errors,
}: AudioDictationFieldsProps) {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generateError, setGenerateError] = React.useState<string | null>(null);

  const handleGenerateAudio = async () => {
    if (!onGenerateAudio) return;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const url = await onGenerateAudio();
      onChange({ audioUrl: url });
    } catch {
      setGenerateError('Error al generar el audio. Inténtalo de nuevo.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Escuchar audio y escribir la palabra</h3>
      <p className={styles.typeFields__description}>
        La IA genera un clip de audio con la palabra. El alumno escucha y escribe lo que oye. Usa una sola palabra clave del script.
      </p>

      <div>
        <label htmlFor="audioTextDictation" className={styles.typeFields__title}>
          Palabra del audio (texto del script)
        </label>
        <Input
          id="audioTextDictation"
          value={audioText}
          onChange={(e) => onChange({ audioText: e.target.value })}
          placeholder="Ej: responsibility"
        />
        <p className={styles.typeFields__hint}>
          Una sola palabra clave que aparece en el script de la historia.
        </p>
        {errors?.audioText && (
          <span className={styles.typeFields__error}>{errors.audioText}</span>
        )}
      </div>

      <div className={styles.typeFields__audioRow}>
        {audioUrl ? (
          <div className={styles.typeFields__audioStatus}>
            <span className={styles.typeFields__audioUrl} title={audioUrl}>
              Audio: {audioUrl.split('/').pop()}
            </span>
            {onGenerateAudio && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Volume2 />}
                onClick={handleGenerateAudio}
                disabled={isGenerating || !audioText.trim()}
              >
                {isGenerating ? 'Generando...' : 'Regenerar audio'}
              </Button>
            )}
          </div>
        ) : onGenerateAudio ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<Volume2 />}
            onClick={handleGenerateAudio}
            disabled={isGenerating || !audioText.trim()}
          >
            {isGenerating ? 'Generando...' : 'Generar audio'}
          </Button>
        ) : (
          <p className={styles.typeFields__hint}>
            Guarda la actividad primero para poder generar el audio.
          </p>
        )}
        {generateError && (
          <span className={styles.typeFields__error}>{generateError}</span>
        )}
      </div>

      <div>
        <label htmlFor="singleCorrectAnswer" className={styles.typeFields__title}>
          Respuesta correcta
        </label>
        <Input
          id="singleCorrectAnswer"
          value={singleCorrectAnswer}
          onChange={(e) => onChange({ singleCorrectAnswer: e.target.value })}
          placeholder="Ej: responsibility"
        />
        <p className={styles.typeFields__hint}>
          En minúsculas, sin puntuación. Debe coincidir exactamente con lo que escribirá el alumno.
        </p>
        {errors?.singleCorrectAnswer && (
          <span className={styles.typeFields__error}>{errors.singleCorrectAnswer}</span>
        )}
      </div>
    </div>
  );
}
